import { CropFree as CropFreeIcon } from "@mui/icons-material";
import {
  Autocomplete,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { DateFilterType, FilterDateSelect } from "components/FilterDateSelect";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import StyledTextField from "components/StyledTextField";
import { endOfDay } from "date-fns";
import { scanditLicense } from "env";
import { addressToPostalLocation, addressToString } from "helpers";
import { useDebounce } from "hooks/useDebounce";
import {
  HistoryFilterDateTypes,
  SearchHistoryFilter,
  SearchHistoryFilterStorageKey,
} from "models/HistoryFilter";
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  getCustomer,
  getCustomerByMachine,
  getCustomers,
  getMachines,
  resetSearchHistoryFilter,
  setSearchHistoryFilter,
} from "store/slices/history.store";

const StyledDetails = styled("div")(() => ({
  marginTop: 5,
  marginLeft: 14,
}));

type SearchHistoryFilterDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const SearchHistoryFilterDialog: FC<SearchHistoryFilterDialogProps> = (props) => {
  const { open, setOpen } = props;
  const intl = useIntl();
  const hasBarcodeScanner = !!scanditLicense();
  const dispatch = useAppDispatch();
  const {
    searchHistoryFilter: filter,
    customers,
    customersLoading,
    machines,
    machinesLoading,
  } = useAppSelector((state) => state.history);
  const [machineACOpen, setMachineACOpen] = useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const [scannerValue, setScannerValue] = useState<string>("");
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [machineSearch, setMachineSearch] = useState<string>("");
  const debouncedMachineSearch = useDebounce<string>(machineSearch, 500);
  const debouncedCustomerSearch = useDebounce<string>(customerSearch, 500);

  const dateOptions: DateFilterType[] = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "filter.last7days" }),
        value: "week",
      },
      {
        label: intl.formatMessage({ id: "filter.lastMonth" }),
        value: "1month",
      },
      {
        label: intl.formatMessage({ id: "filter.last12months" }),
        value: "12month",
      },
      {
        label: intl.formatMessage({ id: "filter.last24months" }),
        value: "24month",
      },
      {
        label: intl.formatMessage({ id: "filter.last5years" }),
        value: "5year",
      },
      {
        label: intl.formatMessage({ id: "filter.specificDate" }),
        value: "specificDate",
      },
    ],
    [intl],
  );

  const setFilter = useCallback(
    (filter: SearchHistoryFilter) => {
      dispatch(setSearchHistoryFilter({ searchHistoryFilter: filter }));
    },
    [dispatch],
  );
  const resetFilter = useCallback(() => {
    dispatch(resetSearchHistoryFilter());
  }, [dispatch]);
  const saveFilter = useCallback(() => {
    localStorage.setItem(SearchHistoryFilterStorageKey, JSON.stringify(filter));
  }, [filter]);

  const setCustomer = useCallback(
    async (
      id: string | null | undefined,
      name: string | null | undefined,
      machineGuid: string | null | undefined,
    ) => {
      if (id) {
        let customer = customers.find((x) => x.id === id);
        if (!customer) {
          if (machineGuid) {
            await dispatch(getCustomerByMachine({ machineGuid: machineGuid }));
          } else if (name) {
            await dispatch(getCustomer({ nameFilter: name }));
          }
          customer = customers.find((x) => x.id === id);
        }
        if (customer) {
          setFilter({
            ...filter,
            customer,
          });
        } else {
          console.log("Something went wrong");
        }
      }
    },
    [customers, dispatch, filter, setFilter],
  );

  const setDate = useCallback(
    (type: HistoryFilterDateTypes) => {
      var specificDate = null;
      if (type === "specificDate") specificDate = filter.specificDate;
      setFilter({
        ...filter,
        typeDate: type,
        specificDate: specificDate,
      });
    },
    [filter, setFilter],
  );
  const setSpecificDate = useCallback(
    (value: Date | null) => {
      setFilter({
        ...filter,
        specificDate: value ? endOfDay(value) : null,
        typeDate: "specificDate",
      });
    },
    [filter, setFilter],
  );

  useEffect(() => {
    if (open) {
      dispatch(
        getMachines({
          machineNo: debouncedMachineSearch,
          customerId: filter.customer?.id,
        }),
      );
    }
  }, [debouncedMachineSearch, open, dispatch, filter.customer?.id]);

  useEffect(() => {
    if (open) {
      dispatch(getCustomers({ nameFilter: debouncedCustomerSearch }));
    }
  }, [debouncedCustomerSearch, dispatch, open]);

  useEffect(() => {
    if (open) {
      if (filter.machine?.id) {
        if (filter.machine.customerId && filter.customer?.id !== filter.machine.customerId) {
          setCustomer(filter.machine.customerId, filter.machine.customerName, filter.machine.id);
        }
      }
    }
  }, [open, setCustomer, dispatch, filter]);

  return (
    <FullScreenDialog
      title={intl.formatMessage({ id: "filter.setFilters" })}
      open={open}
      setOpen={setOpen}
      child={
        <Container sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
              <Grid item width="100%">
                <Autocomplete
                  data-testid="SearchHistoryFilterDialog-MachineFilter"
                  open={machineACOpen}
                  onOpen={() => setMachineACOpen(true)}
                  onClose={() => setMachineACOpen(false)}
                  value={filter.machine}
                  onChange={(_, value, reason) => {
                    if (reason === "clear") {
                      setFilter({
                        ...filter,
                        machine: null,
                      });
                      setMachineSearch("");
                    } else {
                      if (value) {
                        setFilter({
                          ...filter,
                          machine: value,
                        });
                      }
                    }
                  }}
                  getOptionLabel={(o) => `${o.machineNo}`}
                  renderOption={(props, option) => {
                    return (
                      <li
                        {...props}
                        key={`${option.id}-${option.name}`}
                        style={{ display: "block" }}
                      >
                        <Typography variant="body1">{option.machineNo}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {option.idNo}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {option.location}
                        </Typography>
                      </li>
                    );
                  }}
                  inputValue={machineSearch}
                  onInputChange={(_, value) => {
                    if (scannerValue && !value) {
                      setMachineSearch(scannerValue);
                    } else {
                      setScannerValue("");
                      setMachineSearch(value);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value && value.id ? option.id === value.id : true
                  }
                  filterOptions={(x) => x}
                  options={machines}
                  loading={machinesLoading}
                  renderInput={(params) => (
                    <StyledTextField
                      name="machine"
                      {...params}
                      label={intl.formatMessage({
                        id: "job.machine.machineNo",
                      })}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {machinesLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {!!filter.machine?.id && (
                  <StyledDetails>
                    {!!filter.machine.location && (
                      <Typography variant="body2" color="textSecondary">
                        {filter.machine.location}
                      </Typography>
                    )}
                    {!!filter.machine.name && (
                      <Typography variant="body2" color="textSecondary">
                        {filter.machine.name}
                      </Typography>
                    )}
                    {!!filter.machine.idNo && (
                      <Typography variant="body2" color="textSecondary">
                        {filter.machine.idNo}
                      </Typography>
                    )}
                    {!!filter.machine.address && (
                      <Typography variant="body2" color="textSecondary">
                        {addressToString(filter.machine.address)}
                      </Typography>
                    )}
                  </StyledDetails>
                )}
              </Grid>
              {hasBarcodeScanner && (
                <Grid item>
                  <IconButton
                    onClick={() => setOpenScanner(true)}
                    size="large"
                    sx={{ pr: 0, ml: 1 }}
                  >
                    <CropFreeIcon fontSize="large" />
                  </IconButton>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                data-testid="SearchHistoryFilterDialog-CustomerFilter"
                disableClearable={!!filter.machine?.id}
                value={filter.customer}
                onChange={(_, value, reason) => {
                  if (reason === "clear") {
                    setFilter({
                      ...filter,
                      customer: null,
                    });
                  } else {
                    if (value) {
                      setFilter({
                        ...filter,
                        customer: value,
                      });
                    }
                  }
                }}
                getOptionLabel={(o) => `${o.name}`}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.id}-${option.name}`} style={{ display: "block" }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.customerNo}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {addressToPostalLocation(option.address)}
                    </Typography>
                  </li>
                )}
                inputValue={customerSearch}
                onInputChange={(_, value) => {
                  setCustomerSearch(value);
                }}
                isOptionEqualToValue={(option, value) =>
                  value && value.id ? option.id === value.id : true
                }
                filterOptions={(x) => x}
                options={customers}
                loading={customersLoading}
                renderInput={(params) => (
                  <StyledTextField
                    name="customer"
                    {...params}
                    label={intl.formatMessage({
                      id: "general.customer",
                    })}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Fragment>
                          {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </Fragment>
                      ),
                    }}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FilterDateSelect value={filter.typeDate} options={dateOptions} onChange={setDate} />
            </Grid>
            {filter.typeDate === "specificDate" && (
              <Grid item xs={12}>
                <AsolviDatePicker
                  data-testid="ServiceHistoryFilterDialog-DatePickerFilter"
                  onChange={(value) => {
                    setSpecificDate(value);
                  }}
                  value={filter.specificDate}
                  label={intl.formatMessage({ id: "filter.specificDate" })}
                  clearable
                />
              </Grid>
            )}

            <Grid item xs={12} mt={2}>
              <SecondaryButton
                onClick={resetFilter}
                data-testid="SearchHistoryFilterDialog-ResetButton"
              >
                <FormattedMessage id="filter.reset" />
              </SecondaryButton>
            </Grid>
            <Grid item xs={12}>
              <PrimaryButton
                data-testid="SearchHistoryFilterDialog-SubmitButton"
                onClick={() => {
                  saveFilter();
                  setOpen(false);
                }}
              >
                <FormattedMessage id={"history.viewServiceHistory"} />
              </PrimaryButton>
            </Grid>
          </Grid>
          {hasBarcodeScanner && (
            <BarcodeScannerDialog
              open={openScanner}
              setOpen={(open: boolean) => setOpenScanner(open)}
              submitValue={(value: string) => {
                setScannerValue(value);
                setMachineSearch(value);
                setOpenScanner(false);
                setMachineACOpen(true);
              }}
            />
          )}
        </Container>
      }
    />
  );
};
